import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';

const AUTH_TOKEN_KEY = 'risklo_auth_token';

function authHeaders(extra = {}) {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function maskKey(apiKey) {
  const k = String(apiKey || '');
  if (k.length <= 12) return '••••••••';
  return `${k.substring(0, 8)}••••${k.slice(-4)}`;
}

function formatKeyDate(value) {
  if (!value) return 'Never';
  try {
    return new Date(value).toLocaleString();
  } catch (_e) {
    return String(value);
  }
}

/**
 * Generate and manage RiskLo Connector API keys (for NinjaTrader 8 push sync).
 */
export default function ConnectorApiKeyManager({ userEmail, active = true }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [status, setStatus] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);

  const activeKeys = keys.filter((k) => k.is_active && k.api_key);

  const fetchKeys = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const resp = await fetch(API_ENDPOINTS.connectorKeysList, { headers: authHeaders() });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.success) {
        setKeys(data.keys || []);
      }
    } catch (_e) {
      setStatus({ ok: false, msg: 'Could not load API keys.' });
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (active && userEmail) fetchKeys();
  }, [active, userEmail, fetchKeys]);

  const copyKey = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus({ ok: true, msg: 'Copied to clipboard.' });
    } catch (_e) {
      setStatus({ ok: false, msg: 'Could not copy to clipboard.' });
    }
  };

  const handleGenerate = async () => {
    if (!userEmail) return;
    setGenerating(true);
    setStatus(null);
    setNewKeyValue(null);
    try {
      const resp = await fetch(API_ENDPOINTS.connectorKeysGenerate, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          keyName: keyName.trim() || 'My NinjaTrader',
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate API key.');
      }
      setNewKeyValue(data.key?.api_key || null);
      setKeyName('');
      await fetchKeys();
      setStatus({ ok: true, msg: 'API key generated — copy it into the NT8 add-on.' });
    } catch (e) {
      setStatus({ ok: false, msg: e.message || 'Failed to generate API key.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleDeactivate = async (keyId) => {
    if (!userEmail) return;
    if (!window.confirm('Deactivate this API key? The NinjaTrader add-on will stop syncing with it.')) {
      return;
    }
    setDeactivatingId(keyId);
    setStatus(null);
    try {
      const resp = await fetch(API_ENDPOINTS.connectorKeysDelete(keyId), {
        method: 'DELETE',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Failed to deactivate key.');
      }
      if (newKeyValue && keys.find((k) => k.id === keyId)?.api_key === newKeyValue) {
        setNewKeyValue(null);
      }
      await fetchKeys();
      setStatus({ ok: true, msg: 'API key deactivated.' });
    } catch (e) {
      setStatus({ ok: false, msg: e.message || 'Failed to deactivate key.' });
    } finally {
      setDeactivatingId(null);
    }
  };

  if (!userEmail) return null;

  return (
    <div className="veps-api-keys">
      <h4 className="veps-portfolio-title">Connector API Keys</h4>
      <p className="veps-mapping-intro">
        Generate a key for the RiskLo Connector v2 add-on in NinjaTrader 8. Paste the key in{' '}
        <strong>Tools → RiskLo Connector v2</strong> to start pushing account data.
      </p>

      <div className="veps-api-key-generate">
        <span className="veps-api-key-generate-label">Key name (optional)</span>
        <div className="veps-api-key-generate-row">
          <input
            type="text"
            className="veps-input veps-api-key-input"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="My Desktop PC"
            aria-label="Key name (optional)"
          />
          <button
            type="button"
            className="veps-save-btn veps-generate-key-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating…' : 'Generate New API Key'}
          </button>
        </div>
      </div>

      {newKeyValue ? (
        <div className="veps-new-key-banner">
          <span className="veps-new-key-label">New key — copy now (shown once)</span>
          <code className="veps-new-key-value">{newKeyValue}</code>
          <button type="button" className="veps-save-btn veps-save-btn--compact" onClick={() => copyKey(newKeyValue)}>
            Copy
          </button>
        </div>
      ) : null}

      <div className="veps-api-key-list-wrap">
        <div className="veps-api-key-list-head">
          <span>Your keys</span>
          {loading ? <span className="veps-api-key-loading">Loading…</span> : null}
        </div>
        {!loading && activeKeys.length === 0 ? (
          <p className="veps-mapping-hint">No active keys yet.</p>
        ) : null}
        <ul className="veps-api-key-list">
          {activeKeys.map((key) => (
            <li key={key.id} className="veps-api-key-row">
              <div className="veps-api-key-meta">
                <strong>{key.key_name || 'Unnamed key'}</strong>
                <code>{maskKey(key.api_key)}</code>
                <span className="veps-api-key-used">
                  Last used: {formatKeyDate(key.last_used_at)}
                </span>
              </div>
              <div className="veps-api-key-actions">
                <button
                  type="button"
                  className="veps-save-btn veps-save-btn--compact"
                  onClick={() => copyKey(key.api_key)}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="veps-deactivate-btn"
                  onClick={() => handleDeactivate(key.id)}
                  disabled={deactivatingId === key.id}
                >
                  {deactivatingId === key.id ? '…' : 'Deactivate'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {status ? (
        <span className={`veps-status ${status.ok ? 'ok' : 'err'}`}>{status.msg}</span>
      ) : null}
    </div>
  );
}
